import React from 'react';
import { Button, DatePicker, Input, InputNumber, Segmented, Select, Tooltip } from 'antd';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import {
  changeFilterField, changeFilterOperator, createFilterGroup, createFilterRule,
  FILTER_FIELDS, FILTER_LIMITS, getFilterOperators, getFilterStats, SIZE_UNITS,
} from './WaveformFilterBuilder.helpers';
import './WaveformFilterBuilder.css';

function IconButton({ label, icon, ...props }) {
  return <Tooltip title={label}><Button aria-label={label} icon={icon} {...props} /></Tooltip>;
}

function ConditionValue({ rule, onChange }) {
  const between = rule.operator === 'between';
  const setValue = (value, index) => onChange({
    ...rule,
    value: between ? rule.value.map((bound, position) => position === index ? value : bound) : value,
  });
  if (rule.field === 'modified_at') {
    return (
      <div className="waveform-filter-value waveform-filter-date">
        {(between ? rule.value : [rule.value]).map((value, index) => (
          <DatePicker
            key={index}
            aria-label={between ? index === 0 ? '开始时间' : '结束时间' : '条件值'}
            value={value}
            onChange={next => setValue(next, index)}
            showTime={{ format: 'HH:mm' }}
            format="YYYY-MM-DD HH:mm"
          />
        ))}
      </div>
    );
  }
  if (rule.field === 'file_size') {
    return (
      <div className="waveform-filter-value waveform-filter-size">
        {(between ? rule.value : [rule.value]).map((value, index) => (
          <InputNumber
            key={index}
            aria-label={between ? index === 0 ? '最小值' : '最大值' : '条件值'}
            value={value}
            onChange={next => setValue(next, index)}
            min={0}
            step={rule.unit === 'B' ? 1 : 0.1}
          />
        ))}
        <Select
          className="waveform-filter-unit"
          aria-label="大小单位"
          value={rule.unit}
          onChange={unit => onChange({ ...rule, unit })}
          options={Object.keys(SIZE_UNITS).map(value => ({ value, label: value }))}
        />
      </div>
    );
  }
  return (
    <Input
      className="waveform-filter-value"
      aria-label="条件值"
      value={rule.value}
      maxLength={FILTER_LIMITS.text}
      onChange={event => onChange({ ...rule, value: event.target.value })}
      allowClear
    />
  );
}

export default function WaveformFilterBuilder({ value, onChange }) {
  const stats = getFilterStats(value);
  const canAddRule = stats.leaves < FILTER_LIMITS.leaves && stats.nodes < FILTER_LIMITS.nodes;
  const renderGroup = (group, onGroupChange, level, onRemove) => {
    const replace = (id, next) => onGroupChange({
      ...group, rules: group.rules.map(rule => rule.id === id ? next : rule),
    });
    const remove = id => onGroupChange({ ...group, rules: group.rules.filter(rule => rule.id !== id) });
    const add = (node, after) => {
      const rules = [...group.rules];
      rules.splice(after === undefined ? rules.length : after + 1, 0, node);
      onGroupChange({ ...group, rules });
    };
    const canAddGroup = level < FILTER_LIMITS.levels && stats.leaves < FILTER_LIMITS.leaves
      && stats.nodes + 2 <= FILTER_LIMITS.nodes;
    return (
      <div className="waveform-filter-group" key={group.id} role="group" aria-label="条件组">
        <div className="waveform-filter-join">
          <Segmented
            aria-label="组合方式"
            value={group.combinator}
            onChange={combinator => onGroupChange({ ...group, combinator })}
            options={[{ label: 'AND', value: 'and' }, { label: 'OR', value: 'or' }]}
          />
        </div>
        <div className="waveform-filter-branches">
          {group.rules.map((rule, index) => Array.isArray(rule.rules)
            ? renderGroup(rule, next => replace(rule.id, next), level + 1, () => remove(rule.id))
            : (
              <div className="waveform-filter-row" key={rule.id} role="group" aria-label="条件">
                <Select
                  className="waveform-filter-field"
                  aria-label="字段"
                  value={rule.field}
                  options={FILTER_FIELDS}
                  onChange={field => replace(rule.id, changeFilterField(rule, field))}
                />
                <Select
                  className="waveform-filter-operator"
                  aria-label="运算符"
                  value={rule.operator}
                  options={getFilterOperators(rule.field)}
                  onChange={operator => replace(rule.id, changeFilterOperator(rule, operator))}
                />
                <ConditionValue rule={rule} onChange={next => replace(rule.id, next)} />
                <div className="waveform-filter-row-actions">
                  <IconButton label="添加条件行" icon={<PlusOutlined />} disabled={!canAddRule}
                    onClick={() => add(createFilterRule(), index)} />
                  <IconButton label="删除条件" icon={<MinusOutlined />} onClick={() => remove(rule.id)} />
                </div>
              </div>
            ))}
          <div className="waveform-filter-group-actions">
            <Button type="text" icon={<PlusOutlined />} disabled={!canAddRule}
              onClick={() => add(createFilterRule())}>添加条件</Button>
            <Button type="text" icon={<PlusOutlined />} disabled={!canAddGroup}
              onClick={() => add(createFilterGroup())}>添加条件组</Button>
            {onRemove && <IconButton label="删除条件组" type="text" icon={<MinusOutlined />} onClick={onRemove} />}
          </div>
        </div>
      </div>
    );
  };
  return <div className="waveform-filter-builder">{renderGroup(value, onChange, 1)}</div>;
}
